import React, { JSX } from 'react';
import { Box, Container, Heading, Text, SimpleGrid, VStack, Icon, Badge, Flex } from '@chakra-ui/react';
import CtaButton from 'components/CtaButton';
import { ROUTES } from 'services/shared/routerService';
import SEO from 'components/seo';
import ScrollReveal from 'components/ScrollReveal';
import { AD } from 'services/shared/imageService';
import { Star } from 'lucide-react';

function Seedance2Page(): JSX.Element {
	return (
		<Box bg="white" minH="100vh" color="black" overflow="hidden">
			<SEO
				title="Seedance 2.0 | Create AI Video Ads for your brand - Imagemotion"
				description="#1 AI Video Ads Maker. Create stunning ai product and ugc video ads for your e-commerce, dropshipping brand. Try it for free!"
			/>

			{/* Hero Section */}
			<Box as="section" pt={{ base: 20, lg: 32 }} pb={{ base: 24, lg: 40 }} bg="gray.50">
				<Container maxW="1400px" px={8}>
					<ScrollReveal>
						<VStack align="center" textAlign="center" gap={8}>
							<Badge
								px={4}
								py={1.5}
								bg="transparent"
								borderRadius="full"
								color="gray.600"
								fontSize="11px"
								fontWeight="600"
								textTransform="uppercase"
								letterSpacing="0.1em"
							>
								Meet Seedance 2.0
							</Badge>

							<Heading
								as="h1"
								fontSize={{ base: '3rem', md: '5rem' }}
								fontWeight="400"
								lineHeight={1.1}
								letterSpacing="-0.04em"
								maxW="1000px"
							>
								The Future of{' '}
								<Text as="span" color="gray.400">
									AI Video Ads
								</Text>
							</Heading>

							<Text fontSize="xl" color="gray.500" maxW="800px" lineHeight="1.6">
								Seedance 2.0 is live and available here on Imagemotion. The most advanced AI video
								generation model. We bring this cutting-edge technology directly to your ad creative
								workflow.
							</Text>

							<Flex gap={4} wrap="wrap" justify="center" pt={4}>
								<CtaButton href={ROUTES.REGISTER} showIcon bg="black" color="white" hoverScale>
									Try Seedance 2.0 Free
								</CtaButton>
							</Flex>
						</VStack>
					</ScrollReveal>
				</Container>
			</Box>

			{/* Features Section */}
			<Box py={{ base: 24, lg: 32 }} px={8} bg="white">
				<Container maxW="1400px" px={0}>
					<ScrollReveal>
						<VStack gap={16} align="stretch">
							<Box textAlign="center">
								<Badge
									px={4}
									py={1.5}
									bg="transparent"
									borderRadius="full"
									color="gray.600"
									fontSize="11px"
									fontWeight="600"
									textTransform="uppercase"
									letterSpacing="0.1em"
									mb={6}
								>
									Why It&apos;s Different
								</Badge>
								<Heading
									as="h2"
									fontSize={{ base: '2.5rem', md: '4rem' }}
									fontWeight="400"
									letterSpacing="-0.04em"
									lineHeight="1.1"
									mb={8}
								>
									Seedance 2.0 now available
								</Heading>
								<Text fontSize="xl" color="gray.500" maxW="900px" mx="auto" lineHeight="1.6">
									Seedance 2.0 represents the latest AI video synthesis. With unmatched temporal
									consistency, photorealistic textures, and lighting physics that perfectly mimic
									real-world environments, it is the model that global brands and top-tier agencies
									are scrambling to adopt.
								</Text>
							</Box>
						</VStack>
					</ScrollReveal>
				</Container>
			</Box>

			{/* Visual Showcase Section */}
			<Box py={{ base: 24, lg: 32 }} px={8} bg="black" position="relative">
				<Container maxW="1400px" px={0}>
					<ScrollReveal>
						<VStack gap={20} align="stretch">
							<Box textAlign="center">
								<Badge
									bg="whiteAlpha.200"
									color="white"
									borderRadius="full"
									px={4}
									py={1.5}
									fontWeight="600"
									fontSize="11px"
									textTransform="uppercase"
									letterSpacing="0.1em"
									mb={6}
								>
									See It In Action
								</Badge>
								<Heading
									as="h2"
									color="white"
									fontWeight="400"
									fontSize={{ base: '2.5rem', md: '4rem' }}
									letterSpacing="-0.04em"
									lineHeight="1.1"
									mb={8}
								>
									Real Results, Real Products
								</Heading>
								<Text fontSize="xl" color="whiteAlpha.600" maxW="900px" mx="auto" lineHeight="1.6">
									At Imagemotion, we have integrated Seedance 2.0 into our core studio. Now, you
									don&apos;t need a massive budget or a production crew to harness this power. Upload
									your product, let Seedance 2.0 handle the visuals, and watch your ROAS climb.
								</Text>
							</Box>

							<SimpleGrid columns={{ base: 1, md: 2 }} gap={10}>
								{[
									{
										image: AD.CHAIN_TEEN_SOFA_AD,
										title: 'Realistic Product Video Ad',
										label: 'Premium',
									},
									{ image: AD.CUSTOM_NIKE_SHOE, title: 'Cinematic Commercial', label: 'Cinematic' },
									{
										image: AD.CAMERA_AD,
										title: 'E-commerce Demo',
										label: 'E-commerce',
									},
									{ image: AD.CHANEL_AD, title: 'Brand Story', label: 'Brand' },
								].map((item, idx) => (
									<ScrollReveal key={idx} delay={idx * 0.1}>
										<Box
											position="relative"
											borderRadius="3xl"
											overflow="hidden"
											bg="black"
											border="1px solid"
											borderColor="whiteAlpha.200"
											transition="all 0.5s cubic-bezier(0.16, 1, 0.3, 1)"
											_hover={{
												shadow: '0 30px 60px -12px rgba(0,0,0,0.5)',
												transform: 'translateY(-4px)',
											}}
										>
											<Box height={{ base: '300px', md: '450px' }} position="relative">
												<video
													src={item.image}
													autoPlay
													muted
													loop
													playsInline
													style={{
														width: '100%',
														height: '100%',
														objectFit: 'cover',
													}}
												/>
												<Box position="absolute" top={5} left={5}>
													<Badge
														bg="white"
														color="black"
														fontWeight="700"
														fontSize="10px"
														px={3}
														py={1}
														borderRadius="full"
													>
														{item.label}
													</Badge>
												</Box>
												<Box
													position="absolute"
													bottom={0}
													left={0}
													right={0}
													bgGradient="linear(to-t, blackAlpha.800, transparent 60%)"
													p={8}
												>
													<Text color="white" fontWeight="600" fontSize="xl">
														{item.title}
													</Text>
												</Box>
											</Box>
										</Box>
									</ScrollReveal>
								))}
							</SimpleGrid>
						</VStack>
					</ScrollReveal>
				</Container>
			</Box>

			{/* CTA Section */}
			<Box py={{ base: 24, lg: 40 }} px={8} bg="white">
				<Container maxW="1200px" px={0}>
					<ScrollReveal>
						<Box
							position="relative"
							overflow="hidden"
							borderRadius="3xl"
							bg="black"
							color="white"
							p={{ base: 12, md: 24 }}
							textAlign="center"
						>
							<VStack position="relative" zIndex={1} gap={10}>
								<Heading
									as="h2"
									fontSize={{ base: '2.5rem', md: '4.5rem' }}
									fontWeight="400"
									letterSpacing="-0.04em"
									lineHeight="1"
								>
									Ready to Transform Your Ads?
								</Heading>
								<Text fontSize="xl" color="whiteAlpha.600" maxW="800px" lineHeight="1.6">
									Join thousands of e-commerce entrepreneurs who are already using Seedance 2.0 to
									create high-converting video ads in minutes.
								</Text>
								<CtaButton href={ROUTES.REGISTER} bg="white" color="black" hoverScale>
									Start Creating for Free
								</CtaButton>
								<Flex gap={10} align="center" justify="center" pt={4} wrap="wrap">
									<Flex gap={1}>
										{[...Array(5)].map((_, i) => (
											<Icon key={i} as={Star} boxSize={5} color="#FFD700" fill="currentColor" />
										))}
									</Flex>
									<Text fontSize="sm" color="whiteAlpha.700" fontWeight="600">
										<Text as="span" fontWeight="800" color="white">
											10,000+
										</Text>{' '}
										brands scaling with us
									</Text>
								</Flex>
							</VStack>
						</Box>
					</ScrollReveal>
				</Container>
			</Box>
		</Box>
	);
}

export default Seedance2Page;
